import { defineStore } from 'pinia';
import { api } from '../boot/axios';
import { useAuthStore } from './auth';
import { useMedicationStore } from './medication';

export const useLocationStore = defineStore('location', {
  persist: true,

  state: () => ({
    clientLocationsAreLoading: false,
    clientLocationIsLoading: false,
    clientLocations: [],
  }),

  getters: {
    getClientLocationById: (state) => (clientLocationId) => {
      return (
        state.clientLocations.find(
          (clientLocation) => clientLocation.clientLocationID == clientLocationId,
        ) || {}
      );
    },
    getClientLocations: (state) => {
      const locations = [];
      for (var i = 0; i < state.clientLocations.length; i++) {
        locations.push({
          label: state.clientLocations[i].clientLocationName,
          value: parseInt(state.clientLocations[i].clientLocationID),
        });
      }
      return locations;
    },
    // Building out ability to have multiple clients in store.
    getClientLocationsByClientID: (state) => (clientID) => {
      return state.clientLocations.filter((location) => location.clientID === clientID);
    },
    getUserClientLocations: () => {
      const authStore = useAuthStore();
      return this.getClientLocationsByClientID(authStore.clientId);
    },
  },

  actions: {
    saveLocationLocally(newClientLocation) {
      let present = false;
      if (this.clientLocations.length > 0) {
        for (let i = 0; i < this.clientLocations.length; i++) {
          if (this.clientLocations[i].clientLocationID === newClientLocation.clientLocationID) {
            // adding recently found
            Object.assign(this.clientLocations[i], newClientLocation);
            present = true;
          }
        }
      }

      // If not already present, add them to our store
      if (!present) {
        this.clientLocations.push(newClientLocation);
      }

      this.clientLocationIsLoading = false;
    },
    async dataRefresh() {
      // dispatch(GET_CLIENT_LOCATIONS)
      await this.fetchLocations();
    },
    fetchLocation(options) {
      // Check if we have already downloaded it
      return new Promise((resolve, reject) => {
        const foundClientLocation = this.getClientLocationById(options.clientLocationID);
        if (foundClientLocation.clientLocationID) {
          resolve(foundClientLocation);
        }
        // We don't have it, download it
        const authStore = useAuthStore();
        let clientId = authStore.getUser.clientID;
        if (options.clientID) {
          clientId = options.clientID;
        }
        this.clientLocationIsLoading = true;
        api
          .get('api/v1/clients/' + clientId + '/locations/' + options.clientLocationID)
          .then((response) => {
            this.saveLocationLocally(response.data);
            const medStore = useMedicationStore();
            if (response.data.locationUsesHealthCanadaMedicationList) {
              medStore.fetchGenericMedications();
            } else {
              medStore.fetchLocationMedications(response.data.clientLocationID, clientId);
            }
            resolve(response.data);
          })
          .catch((err) => {
            this.clientLocationIsLoading = false;
            reject(err);
          });
      });
    },
    fetchLocations() {
      // commit(GET_CLIENT_LOCATIONS_PENDING)
      this.clientLocationsAreLoading = true;
      return new Promise((resolve, reject) => {
        const authStore = useAuthStore();
        api
          .get('api/v1/clients/' + authStore.clientId + '/locations')
          .then((response) => {
            for (let i = 0; i < response.data.length; i++) {
              // commit(GET_CLIENT_LOCATION_SUCCESS, response.data[i])
              this.saveLocationLocally(response.data[i]);
              if (
                authStore.isClientAdmin ||
                (authStore.isLocationAdmin &&
                  authStore.user.clientLocationID === response.data[i].clientLocationID) ||
                (authStore.isMedicalAdmin &&
                  authStore.user.clientLocationID === response.data[i].clientLocationID)
              ) {
                // dispatch(GET_LOCATION_MEDICATIONS, { clientLocationID: response.data[i].clientLocationID })
                const medStore = useMedicationStore();
                medStore.fetchLocationMedications(
                  response.data[i].clientLocationID,
                  authStore.clientId,
                );
              }
            }
            // commit(GET_CLIENT_LOCATIONS_SUCCESS)
            resolve(response.data);
          })
          .catch((err) => {
            // commit(GET_CLIENT_LOCATIONS_FAILURE, err)
            reject(err);
          })
          .finally(() => {
            this.clientLocationsAreLoading = false;
          });
      });
    },
    async fetchExternalClientLocations(clientID, getInactiveLocations = false) {
      this.clientLocationsAreLoading = true;

      const url = getInactiveLocations
        ? 'api/v1/clients/' + clientID + '/locations/?show-inactive=true'
        : 'api/v1/clients/' + clientID + '/locations';

      await api
        .get(url)
        .then(async (response) => {
          for (let i = 0; i < response.data.length; i++) {
            this.saveLocationLocally(response.data[i]);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.clientLocationsAreLoading = false;
        });
    },
    postLocation(options) {
      return new Promise((resolve, reject) => {
        const authStore = useAuthStore();
        api
          .put(
            'api/v1/clients/' + authStore.clientId + '/locations/' + options.clientLocationID,
            options,
          )
          .then((response) => {
            // commit(GET_CLIENT_LOCATION_SUCCESS, response.data)
            this.saveLocationLocally(response.data);
            resolve(response);
          })
          .catch((err) => {
            // PRX-2425: reject so the screen can show the error (e.g. 409 after location cutover).
            reject(err);
          });
      });
    },
    fetchCallInSheet(options) {
      return new Promise((resolve, reject) => {
        api({
          url: 'api/v1/documents/callinsheet/' + options.clientLocationID,
          method: 'GET',
          responseType: 'blob', // important
        })
          .then((response) => {
            resolve(response);
          })
          .catch((err) => {
            console.log(err);
            reject(err);
          });
      });
    },
    fetchPreMedicalAssessment() {
      return new Promise((resolve, reject) => {
        api({
          url: 'api/v1/documents/templates',
          method: 'GET',
          params: {
            templateName: 'premedicalassessment.pdf',
          },
          responseType: 'blob', // premedicalassessment.pdf
        })
          .then((response) => {
            resolve(response);
          })
          .catch((err) => {
            console.log(err);
            reject(err);
          });
      });
    },
  },
});

/*
 * Then in the component, change the import to:

import { useMedicationStore } from '../stores/medication'

 * Use the new Vue3 setup method to connect to the store:

   setup () {
    const medicationStore = useMedicationStore()
    return { medicationStore }
  },

 * Change your use of the store from
 *

 this.$store.dispatch(MEDICATION_SAVE, this.localMedication)

 * to

 this.medicationStore.saveMedication(this.localMedication)

 */
